import { useEffect } from "react";

import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Rating from "@mui/material/Rating";
import Typography from "@mui/material/Typography";

import { RecipeReviewInterface } from "../../app/recipePage/recipePage.types";
import RatingsSummary from "../../components/recipePage/RatingsSummary";
import useCheckHistory from "../../hooks/useCheckHistory";
import useRecipeReviews from "../../hooks/useRecipeReviews";
import { RecentRatingsIcon } from "../../iconRef/recipePageIcons";
import { BackIcon } from "../../utils/iconRefs/recipeStepsIcons";
import styles from "./recipeReviewPage.module.css";
import RecipeReviewPageSkeleton from "./RecipeReviewPageSkeleton";

const timeAgo = (startTime: string | undefined) => {
  if (!startTime) return "";

  const now = new Date();
  const start = new Date(startTime);
  const diffInMs = now.getTime() - start.getTime();

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) return diffInMinutes <= 1 ? "Cooked 1 min ago" : `Cooked ${diffInMinutes} mins ago`;
  if (diffInHours < 24) return diffInHours === 1 ? "Cooked 1 hr ago" : `Cooked ${diffInHours} hrs ago`;
  return diffInDays === 1 ? "Cooked 1 day ago" : `Cooked ${diffInDays} days ago`;
}

const RecipeReviewPage = () => {
  const { ratingsSummary, reviews, recipeName, loading: reviewsLoading } = useRecipeReviews();
  const goBackOrRedirect = useCheckHistory();

  const handleBackClick = () => goBackOrRedirect('/');

  useEffect(() => {
    // TODO: Need to RCA this
    window.scrollTo(0, 0);
  }, []);

  if (reviewsLoading) return <RecipeReviewPageSkeleton />;

  return (
    <Grid className={styles.reviews_container}>
      <AppBar className={styles.nav}>
        <Grid className={styles.back_arrow} onClick={() => handleBackClick()}>
          <BackIcon />
        </Grid>
        <Typography className={styles.heading_text}>{recipeName}</Typography>
        <Grid className={styles.review_back_button} />
      </AppBar>
      <Grid className={styles.reviews_below_appbar}>
        <RatingsSummary ratingsSummary={ratingsSummary} source="reviewPage" />

        <Grid>
          <Grid className={styles.recent_ratings_title}>
            <RecentRatingsIcon />
            <Typography variant="body5">Customer Reviews</Typography>
          </Grid>
          <Grid className={styles.reviews_wrapper}>
            {reviews.map((review: RecipeReviewInterface) => {
              const timeAgoText = timeAgo(review?.startTime);
              const firstLetterOfName = review?.user?.nickName?.charAt(0).toUpperCase();
              return (
                <Grid className={styles.review_card_container}>
                  <Grid className={styles.review_card_header}>
                    <Grid className={styles.users_data}>
                      {review?.user?.photoURL ? <Avatar
                        alt="userAvatar"
                        src={review?.user?.photoURL}
                      /> : <Avatar alt="userAvatar">{firstLetterOfName}</Avatar>}
                      <Grid>
                        <Grid className={styles.user_name_rating}>
                          <Typography variant="captionparalell">{review?.user?.nickName}</Typography>
                          <Typography variant="body2paralell">{timeAgoText}</Typography>
                        </Grid>
                        <Rating name="read-only" readOnly size="small"
                          value={review?.rating} />
                      </Grid>
                    </Grid>
                  </Grid>
                  {review?.feedback?.length > 0 ? <Grid className={styles.rating_feedback_container}>
                    {review?.feedback?.map((feedback: string) => (
                      <Grid className={styles.rating_feedback}>
                        <Typography variant="caption4Light">{feedback}</Typography>
                      </Grid>
                    ))}
                  </Grid> : null}
                  {review?.userComments ? <Grid className={styles.user_comments}>
                    <Typography variant="caption4Light">{review?.userComments}</Typography>
                  </Grid> : null}
                </Grid>
              )
            })}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default RecipeReviewPage;